import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  AppWindowIcon,
  BrowsersIcon,
  MonitorIcon,
} from '@phosphor-icons/react';
import { EmojiAvatar } from '@/components/emoji-avatar';
import { EventFeed } from '@/components/event-feed';
import { Badge } from '@/components/ui/badge';
import { ProfileSections, ProfileInfoCard } from '@/components/profiles';
import { DatePicker } from '@/components/date-picker';
import { getCountry } from '@/utils/countries';
import { getFlagEmoji } from '@/utils/get-flag-emoji';
import { useProjects } from '@/hooks/use-projects';
import { useDateRange } from '@/hooks/use-date-range';
import {
  customerProfileIdentityOptions,
  infiniteEventsOptions,
  profileMetricsOptions,
  profileDeviceGeoOptions,
} from '@/hooks/query-options';
import { PageContent } from '@/components/page-content';

export const Route = createFileRoute('/_auth/_side-layout/users_/$userId')({
  component: UserDetails,
  loader: async ({ context, params }) => {
    const projectId = context.projectId;
    const userId = params.userId;
    const filterGroups = [
      {
        filters: [
          {
            fieldName: 'user_id',
            operator: '=',
            value: userId,
          },
        ],
      },
    ];
    const selectedEvent = null;
    if (!projectId || !userId) {
      return;
    }
    // No profile-metrics prefetch: the component keys that query by the
    // active date range's from/to ISO strings (derived from useDateRange at
    // render), which a range-less loader prefetch can't reproduce — so it
    // would warm a key the component never reads. Let the component fetch it.
    context.queryClient.prefetchInfiniteQuery(
      infiniteEventsOptions({
        projectId,
        filterGroups,
        selectedEvent,
      }),
    );
  },
});

// At-a-glance identity strip under the name: raw user_id (when led by email) +
// last-seen location and device, mirroring the competitor's compact header.
// Renders only the facts we actually have — no "—" placeholders here.
function IdentitySummaryRow({
  userId,
  email,
  current,
}: {
  userId: string;
  email: string | null;
  current: {
    country: string | null;
    city: string | null;
    device: string | null;
    os: string | null;
    browser: string | null;
  } | null;
}) {
  const country = current?.country ?? null;
  const items: React.ReactNode[] = [];

  if (email) {
    items.push(
      <span key="uid" className="font-mono">
        {userId}
      </span>,
    );
  }
  if (country) {
    items.push(
      <span key="loc" className="flex items-center gap-1.5">
        <span>{getFlagEmoji(country)}</span>
        <span>
          {getCountry(country) ?? country}
          {current?.city ? ` · ${current.city}` : ''}
        </span>
      </span>,
    );
  }
  if (current?.device) {
    items.push(
      <span key="device" className="flex items-center gap-1.5">
        <MonitorIcon size={14} />
        {current.device}
      </span>,
    );
  }
  if (current?.os) {
    items.push(
      <span key="os" className="flex items-center gap-1.5">
        <AppWindowIcon size={14} />
        {current.os}
      </span>,
    );
  }
  if (current?.browser) {
    items.push(
      <span key="browser" className="flex items-center gap-1.5">
        <BrowsersIcon size={14} />
        {current.browser}
      </span>,
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      {items}
    </div>
  );
}

function UserDetails() {
  const { userId } = Route.useParams();
  const { projectId } = useProjects();
  const [dateRange] = useDateRange();

  const scope = { kind: 'user' as const, projectId: projectId!, userId };
  const range = {
    from: dateRange.startDate.toISOString(),
    to: dateRange.endDate.toISOString(),
  };

  // Surfaces an "Identified" badge when this user_id resolves to a stitched
  // identity. The profile modules themselves don't need this — the backend
  // resolves user_id → identity transparently (and falls back to user_id
  // scope for anonymous/unidentified users, never 404ing).
  const { data: identity } = useQuery(
    customerProfileIdentityOptions({ projectId: projectId!, userId }),
  );
  // Shared query keys with ProfileSections → these resolve from cache (no extra
  // round-trips). Feed the at-a-glance summary row + the Profile Information
  // card with the same metrics/device-geo the sections below render.
  const { data: metrics } = useQuery(profileMetricsOptions(scope, range));
  const { data: deviceGeo, isLoading: deviceGeoLoading } = useQuery(
    profileDeviceGeoOptions(scope, range),
  );
  const isIdentified = Boolean(identity?.identity_id);
  const current = deviceGeo?.current ?? null;

  return (
    <PageContent>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <EmojiAvatar id={userId} />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-3">
              <h1 className="min-w-0 truncate text-xl font-bold tracking-tight">
                {identity?.email ?? userId}
              </h1>
              {isIdentified ? (
                <Badge variant="indigo" className="shrink-0">
                  Identified
                </Badge>
              ) : (
                <Badge variant="neutral" className="shrink-0">
                  Anonymous
                </Badge>
              )}
            </div>
            <IdentitySummaryRow
              userId={identity?.user_id ?? userId}
              email={identity?.email ?? null}
              current={current}
            />
          </div>
        </div>
        <DatePicker />
      </div>

      <ProfileSections
        scope={scope}
        from={dateRange.startDate}
        to={dateRange.endDate}
        afterStats={
          <ProfileInfoCard
            userId={userId}
            identity={identity}
            metrics={metrics}
            current={current}
            isLoading={deviceGeoLoading}
          />
        }
      />

      <EventFeed
        key={projectId!}
        {...{
          projectId: projectId!,
          hideUserAvatar: true,
          filterGroups: [
            {
              filters: [
                {
                  fieldName: 'user_id',
                  operator: '=',
                  value: userId,
                },
              ],
            },
          ],
        }}
      />
    </PageContent>
  );
}
